import React, { useEffect, useState, useCallback } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Typography, IconButton, Tooltip } from '@mui/material';
import { isEnabledModule } from '#component/project/form/helper';
import DisabledModuleMessage from '#component/project/form/project/project-modules/disabled-module-message';
import {
  AddButtonIcon,
  ButtonContainer,
  StyledButton,
  StyledPaper,
  StyledTitle
} from './styles';
import PackageItem from './package-item';
import { useIntersection } from '#hooks';
import { uuidv4 } from '#helper';

const intersectionOptions = {
  rootMargin: '-69px',
  threshold: [1]
};

/**
 * Renders form with submit, go back
 * and add package buttons.
 * @returns {React.FC}
 */

const PackageForm = props => {
  const {
    previousStep,
    history,
    forceError,
    formModules,
    submitForm,
    saveDisabled
  } = props;
  const { sentinelRef, isIntersecting } = useIntersection(intersectionOptions);
  const { control, watch, handleSubmit, formState: { errors } } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'packages',
    keyName: 'fieldId'
  });
  const items = watch('packages') ?? [];
  const arrayError = errors.packages?.root?.message;
  const shipmentModuleEnabled = isEnabledModule('SHIPMENT', formModules);

  const [expanded, setExpanded] = useState(
    fields.length ? `packages[${fields.length - 1}]` : false
  );

  const handleSetExpanded = useCallback(panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  }, []);

  useEffect(() => {
    if (fields.length) {
      setExpanded(`packages[${fields.length - 1}]`);
    }
  }, [fields.length]);

  const handleCancelClick = () => {
    history.push('/project');
  };

  const addPackageItem = handleSubmit(() => append({ id: uuidv4() }));

  return (
    <form onSubmit={submitForm} autoComplete="off">
      {shipmentModuleEnabled ? (
        <>
          <StyledTitle variant="h4" gutterBottom>
            Package type settings
          </StyledTitle>
          <StyledPaper elevation={0}>
            {fields.map((field, index) => (
              <PackageItem
                key={field.fieldId}
                item={items[index] ?? field}
                index={index}
                member={`packages[${index}]`}
                remove={remove}
                numberOfItems={fields.length}
                expanded={expanded === `packages[${index}]`}
                handleSetExpanded={handleSetExpanded}
                forceError={forceError}
              />
            ))}
            {arrayError &&
              <Typography align="center" color="error">{arrayError}</Typography>}
          </StyledPaper>
        </>
      ) : <DisabledModuleMessage />}
      <div ref={sentinelRef} />
      <ButtonContainer
        isIntersecting={isIntersecting}
        shipmentModuleEnabled={shipmentModuleEnabled}
      >
        {shipmentModuleEnabled ?
          <div>
            <IconButton size="small" onClick={addPackageItem} aria-label="Add package type setting">
              <Tooltip title="Add package type setting">
                <AddButtonIcon />
              </Tooltip>
            </IconButton>
          </div> : null}
        <div>
          <StyledButton variant="outlined" onClick={handleCancelClick}>
            Cancel
          </StyledButton>
          <StyledButton
            variant="outlined"
            onClick={previousStep}
          >
            Back
          </StyledButton>
          <StyledButton
            disabled={saveDisabled}
            type="submit"
            variant="contained"
            color="secondary"
          >
            Save
          </StyledButton>
        </div>
      </ButtonContainer>
    </form>
  );
};

export default PackageForm;
